import React, { useState, useEffect } from 'react';
import { fetchUnfinishedOrders } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';
import { toTitleCase } from '../utils/format';

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

const todayIso = new Date().toISOString().split('T')[0];
const PAGE_SIZE = 50;

export default function UnfinishedOrders({ navigateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(todayIso);
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchUnfinishedOrders({ StartDate: toApiDate(start), EndDate: toApiDate(end), PageNumber: page, PageSize: PAGE_SIZE })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page]);

  const rows = Array.isArray(data) ? data : data?.data ?? data?.records?.data ?? [];
  const total = data?.totalRows ?? data?.total ?? rows.length;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Unfinished Orders</h2>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Date Range</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <DateRangeFilter start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} />
          <button onClick={() => { setPage(1); load(); }} className="btn btn-primary" style={{ fontSize: 11 }}>Search</button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Unfinished Orders {total > 0 && `(${total.toLocaleString()})`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No unfinished orders found.</div>
        )}
        {rows.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order ID</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship Type</th>
                    <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Total</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Status</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship Date</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} onClick={() => navigateTo?.('order-details', row.bcOrderId ?? row.orderId)} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}>
                      <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{row.bcOrderId ?? row.orderId ?? '—'}</td>
                      <td style={{ padding: '8px 8px', color: 'var(--navy)' }}>{toTitleCase(row.billTo ?? row.shipTo ?? row.customerName ?? '—')}</td>
                      <td style={{ padding: '8px 8px', color: 'var(--gray-700)' }}>{row.shipType ?? '—'}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600 }}>${Number(row.orderTotal ?? 0).toFixed(2)}</td>
                      <td style={{ padding: '8px 8px' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: '#fff8e6', color: '#b06a00' }}>
                          {row.status ?? row.orderStatus ?? 'Incomplete'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>
                        {row.shipDate ?? row.startDate ? new Date(row.shipDate ?? row.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {total > PAGE_SIZE && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 12 }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn btn-navy" style={{ fontSize: 11 }}>← Prev</button>
                <span style={{ fontSize: 11, alignSelf: 'center' }}>Page {page}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={rows.length < PAGE_SIZE} className="btn btn-navy" style={{ fontSize: 11 }}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
